import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2, Sparkles, User, Calendar } from 'lucide-react';
import { rehydratePendingInviteContext, logInviteStorageState } from '@/utils/inviteStorage';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { hideKeyboard } from '@/utils/capacitor';
import dayjs from 'dayjs';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

function getAge(dobISO: string) {
  const dob = dayjs(dobISO);
  const now = dayjs();
  let age = now.year() - dob.year();
  if (now.month() < dob.month() || (now.month() === dob.month() && now.date() < dob.date())) {
    age -= 1;
  }
  return age;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

const ITEM_HEIGHT = 44;
const VISIBLE_COUNT = 3;
const ROLLER_HEIGHT = ITEM_HEIGHT * VISIBLE_COUNT;

interface ScrollWheelProps<T> {
  items: T[];
  value: T;
  onChange: (value: T) => void;
  format: (item: T) => string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

function ScrollWheel<T>({ items, value, onChange, format, scrollRef }: ScrollWheelProps<T>) {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    const idx = items.indexOf(value);
    return idx >= 0 ? idx : 0;
  });
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync selectedIndex when value prop changes (e.g. day clamp from parent)
  useEffect(() => {
    let idx = items.indexOf(value);
    if (idx < 0) idx = items.length - 1; // e.g. day 31 when month has 28 days
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    if (clamped !== selectedIndex) {
      setSelectedIndex(clamped);
      scrollRef.current?.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: 'auto' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items, scrollRef]);

  // Initial scroll to selected position
  useEffect(() => {
    if (scrollRef.current && selectedIndex >= 0) {
      scrollRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      const idx = Math.round(scrollRef.current!.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(idx, items.length - 1));
      setSelectedIndex(clamped);
      onChange(items[clamped]);
      scrollRef.current?.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: 'smooth' });
      scrollTimeoutRef.current = null;
    }, 150);
  }, [items, onChange, scrollRef]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden scroll-smooth overscroll-contain"
      style={{
        height: ROLLER_HEIGHT,
        scrollSnapType: 'y mandatory',
        WebkitOverflowScrolling: 'touch',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: ITEM_HEIGHT }} aria-hidden="true" />
      {items.map((item, i) => (
        <div
          key={i}
          className="flex items-center justify-center shrink-0"
          style={{
            height: ITEM_HEIGHT,
            scrollSnapAlign: 'center',
          }}
        >
          <span
            className={`transition-all duration-150 ${
              i === selectedIndex
                ? 'text-white text-2xl font-bold'
                : 'text-white/40 text-lg'
            }`}
          >
            {format(item)}
          </span>
        </div>
      ))}
      <div style={{ height: ITEM_HEIGHT }} aria-hidden="true" />
    </div>
  );
}

const BasicProfileStep: React.FC = () => {
  const { updateData, completeOnboarding } = useOnboarding();
  const { user: authUser } = useAuth();
  const location = useLocation();

  const inviteCodeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('inviteCode');
  }, [location.search]);

  const [name, setName] = useState('');
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18;
  const defaultYear = Math.max(maxYear - 25, currentYear - 100);
  const defaultMonth = 5; // June (0-based)
  const defaultDay = 15;

  const [rollerYear, setRollerYear] = useState(defaultYear);
  const [rollerMonth, setRollerMonth] = useState(defaultMonth);
  const [rollerDay, setRollerDay] = useState(defaultDay);

  const [dateOfBirth, setDateOfBirth] = useState(() =>
    dayjs(new Date(defaultYear, defaultMonth, defaultDay)).format('YYYY-MM-DD')
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const dayRef = useRef<HTMLDivElement>(null);
  const monthRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const years = useMemo(
    () => Array.from({ length: maxYear - (currentYear - 100) + 1 }, (_, i) => maxYear - i),
    [maxYear, currentYear]
  );

  const maxDays = useMemo(
    () => getDaysInMonth(rollerYear, rollerMonth),
    [rollerYear, rollerMonth]
  );

  const days = useMemo(
    () => Array.from({ length: maxDays }, (_, i) => i + 1),
    [maxDays]
  );

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => i),
    []
  );

  const syncDateFromRollers = useCallback((y: number, m: number, d: number) => {
    const maxD = getDaysInMonth(y, m);
    const clampedDay = Math.min(d, maxD);
    const iso = dayjs(new Date(y, m, clampedDay)).format('YYYY-MM-DD');
    setDateOfBirth(iso);
    setRollerDay(clampedDay);
    setError('');
  }, []);

  const handleDayChange = useCallback((d: number) => {
    setRollerDay(d);
    syncDateFromRollers(rollerYear, rollerMonth, d);
  }, [rollerYear, rollerMonth, syncDateFromRollers]);

  const handleMonthChange = useCallback((m: number) => {
    setRollerMonth(m);
    const maxD = getDaysInMonth(rollerYear, m);
    const clampedDay = Math.min(rollerDay, maxD);
    setRollerDay(clampedDay);
    syncDateFromRollers(rollerYear, m, clampedDay);
  }, [rollerYear, rollerDay, syncDateFromRollers]);

  const handleYearChange = useCallback((y: number) => {
    setRollerYear(y);
    const maxD = getDaysInMonth(y, rollerMonth);
    const clampedDay = Math.min(rollerDay, maxD);
    setRollerDay(clampedDay);
    syncDateFromRollers(y, rollerMonth, clampedDay);
  }, [rollerMonth, rollerDay, syncDateFromRollers]);

  // Google onboarding UX: Prefill name from DB profile or Google metadata
  const hasPrefilledRef = useRef(false);
  useEffect(() => {
    if (!authUser?.id || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const metaName =
        (user?.user_metadata?.name as string) ||
        '';

      let profile: { name?: string; date_of_birth?: string } | null = null;
      if (authUser.id) {
        const { data: profileData } = await supabase
          .from('users')
          .select('name, date_of_birth')
          .eq('id', authUser.id)
          .maybeSingle();
        profile = profileData;
      }

      const initialName = (profile?.name || metaName || '').trim();
      if (initialName) setName(initialName);

      if (profile?.date_of_birth) {
        const d = dayjs(profile.date_of_birth, 'YYYY-MM-DD', true);
        if (d.isValid()) {
          setRollerYear(d.year());
          setRollerMonth(d.month());
          setRollerDay(d.date());
          setDateOfBirth(d.format('YYYY-MM-DD'));
        }
      }

      console.info('[onboarding] name_prefill', {
        fromDb: !!profile?.name,
        fromMeta: !!metaName
      });
    })();
  }, [authUser?.id]);

  // Storage checks
  useEffect(() => {
    (async () => {
      await rehydratePendingInviteContext('🚨 BasicProfileStep rehydrate', inviteCodeFromQuery);
      logInviteStorageState('🚨 BasicProfileStep loaded - checking storage');
    })();
  }, [inviteCodeFromQuery]);

  const isNameValid = name.trim().length >= 2;
  const isAndroid = Capacitor.getPlatform() === 'android';

  const blurAndHideKeyboard = useCallback(async () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (isAndroid) {
      await hideKeyboard();
    }
  }, [isAndroid]);

  const advanceToNextStep = useCallback(async () => {
    await blurAndHideKeyboard();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await completeOnboarding();
  }, [blurAndHideKeyboard, completeOnboarding]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;

    setError('');
    const trimmedName = name.trim();

    if (trimmedName.length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }

    const age = getAge(dateOfBirth);
    if (age < 18) {
      setError('You must be at least 18 years old to use BlissNet. This is a strict requirement for all users.');
      return;
    }

    setIsLoading(true);
    try {
      localStorage.setItem('bn_name', trimmedName);
      console.log('✅ V242.3: Name stored in localStorage:', trimmedName);

      updateData({
        name: trimmedName,
        date_of_birth: dateOfBirth,
        dateOfBirth,
      });
      await advanceToNextStep();
    } catch (err) {
      console.error('❌ Error caching basic profile data:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, name, dateOfBirth, advanceToNextStep, updateData]);

  useEffect(() => {
    console.log('📍 BasicProfileStep route:', location.pathname, location.search);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-slate-900 to-black pointer-events-none"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000 pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md space-y-8" onClick={(e) => e.stopPropagation()} onPointerDownCapture={(e) => e.stopPropagation()}>
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-2xl shadow-pink-500/25">
              <Link2 className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent mb-3">
            Welcome to BlissNet!
          </h2>
          <p className="text-slate-300 text-lg">Let's start with the basics</p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl shadow-black/20 space-y-6"
        >
          <div className="space-y-3">
            <Label htmlFor="name" className="text-white font-medium flex items-center gap-2">
              <User className="w-4 h-4" />
              Your Name
            </Label>

            {isAndroid ? (
              <input
                ref={nameInputRef}
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError('');
                }}
                onContextMenu={(e) => e.preventDefault()}
                onSelect={(e) => {
                  const el = e.currentTarget;
                  requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
                }}
                onPointerDownCapture={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                enterKeyHint="done"
                autoComplete="name"
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="words"
                className="h-12 w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 rounded-2xl px-4
                           focus:bg-white/15 focus:border-white/30 transition-all duration-300 outline-none text-base
                           select-none [-webkit-user-select:none] [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent]
                           [touch-action:manipulation] [&::selection]:bg-transparent"
                style={{ caretColor: 'white', fontSize: '16px' }}
                disabled={isLoading}
                autoFocus
              />
            ) : (
              <Input
                ref={nameInputRef}
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError('');
                }}
                enterKeyHint="done"
                inputMode="text"
                autoComplete="name"
                autoCorrect="off"
                autoCapitalize="words"
                spellCheck={false}
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-2xl focus:bg-white/15 focus:border-white/30 transition-all duration-300 caret-white"
                disabled={isLoading}
                autoFocus
              />
            )}
          </div>

          <div className="space-y-3">
            <Label htmlFor="dob" className="text-white font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Date of Birth
            </Label>

            <div
              className="flex items-stretch bg-white/10 border border-white/20 rounded-2xl overflow-hidden"
              onTouchStart={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
            >
              <div className="flex-1 min-w-0 border-r border-white/10">
                <ScrollWheel
                  scrollRef={dayRef}
                  items={days}
                  value={rollerDay}
                  onChange={handleDayChange}
                  format={(d) => String(d).padStart(2, '0')}
                />
              </div>
              <div className="flex-1 min-w-0 border-r border-white/10">
                <ScrollWheel
                  scrollRef={monthRef}
                  items={months}
                  value={rollerMonth}
                  onChange={handleMonthChange}
                  format={(m) => MONTH_NAMES[m]}
                />
              </div>
              <div className="flex-1 min-w-0">
                <ScrollWheel
                  scrollRef={yearRef}
                  items={years}
                  value={rollerYear}
                  onChange={handleYearChange}
                  format={(y) => String(y)}
                />
              </div>
            </div>

            <p className="text-slate-400 text-sm">You must be 18+</p>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-4 text-red-200 text-sm backdrop-blur-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-14 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-pink-500/25 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || !isNameValid}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 mr-3 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default BasicProfileStep;
